import { iconButtonClasses, Theme } from '@mui/material'
import { blue, gray, red, uiColors } from '~/common/styles/colors'
import { depth } from '~/common/styles/depths'
import { IconButtonColor, IconButtonSize, IconButtonVariant } from './../types'

export const getIconButtonPrimitives = (floating: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  borderRadius: '1px',
  boxShadow: floating ? depth[2] : 'none',
  border: '2px solid transparent',
  '&:focus-visible': {
    border: `2px solid ${blue[9]}`,
    outline: `1px solid ${uiColors.content.white}`,
    outlineOffset: '-3px',
  },
  padding: '4px',
})

/**
 * Returns the style of the icon button based on its variant
 */
export const getVariantPrimitives = (
  variant: IconButtonVariant,
  color: IconButtonColor,
  theme: Theme,
) => {
  switch (variant) {
    case 'primary':
      return {
        color: uiColors.content.white,
        backgroundColor: blue[9],
        '&:hover': {
          backgroundColor: blue[10],
          borderRadius: '2px',
        },
        '&:active': {
          backgroundColor: blue[11],
          borderRadius: '2px',
        },
      }
    case 'secondary':
      return {
        color: uiColors.content.white,
        backgroundColor: gray[12],
        '&:hover': {
          backgroundColor: gray[11],
          borderRadius: '2px',
        },
        '&:active': {
          backgroundColor: gray[10],
          borderRadius: '2px',
        },
      }
    case 'tertiary':
      return {
        color: getGhostButtonColor(color),
        backgroundColor: gray[2],
        borderRadius: '2px',
        '&:hover': {
          backgroundColor: gray[3],
          borderRadius: '2px',
        },
        '&:active': {
          backgroundColor: gray[2],
          borderRadius: '2px',
        },
      }
    case 'ghost':
      return {
        color: getGhostButtonColor(color),
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: gray[2],
          borderRadius: '2px',
        },
        '&:active': {
          backgroundColor: gray[3],
          borderRadius: '2px',
        },
      }
    case 'true-ghost':
      return {
        color: getGhostButtonColor(color),
        backgroundColor: 'transparent',
        '&:hover': {
          color: theme.palette.common.doss.gray[9],
          borderRadius: '2px',
        },
        '&:active': {
          backgroundColor: gray[2],
          borderRadius: '2px',
        },
      }
    case 'danger':
      return {
        color: uiColors.content.white,
        backgroundColor: red[10],
        '&:hover': {
          backgroundColor: red[11],
          borderRadius: '2px',
        },
        '&:active': {
          backgroundColor: red[12],
          borderRadius: '2px',
        },
      }
  }
}

/**
 * Returns the color for the ghost icon button based on the color prop
 */
const getGhostButtonColor = (color: IconButtonColor) => {
  switch (color) {
    case 'primary':
      return uiColors.content.primary
    case 'secondary':
      return uiColors.content.secondary
    case 'danger':
      return uiColors.content.danger
    case 'warning':
      return uiColors.content.warning
  }
}

/**
 * Returns the disabled styles for the icon button based on the variant
 */
export const getDisabledVariantPrimitives = (variant: IconButtonVariant) => {
  switch (variant) {
    case 'primary':
    case 'secondary':
    case 'tertiary':
    case 'danger':
      return {
        [`&.${iconButtonClasses.disabled}`]: {
          backgroundColor: gray[3],
          color: uiColors.content.disabled,
        },
      }
    case 'ghost':
    case 'true-ghost':
      return {
        [`&.${iconButtonClasses.disabled}`]: {
          color: uiColors.content.disabled,
        },
      }
  }
}

/**
 * Returns the styles for the icon for an icon butotn based on the button size
 */
export const getIconPrimitives = (size: IconButtonSize) => {
  switch (size) {
    case 'small':
      return {
        height: '16px',
        flexShrink: 0,
        fontSize: '1rem',
      }
    case 'medium':
      return {
        height: '16px',
        flexShrink: 0,
        fontSize: '1.25rem',
      }
    case 'large':
      return {
        height: '24px',
        flexShrink: 0,
        fontSize: '1.5rem',
      }
  }
}
